import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import ProcessingModal from './components/ProcessingModal';
import ServiceChargeModal from './components/ServiceChargeModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ReadAloudButton from './components/ReadAloudButton';
import AudioPlayer from './components/AudioPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

function BRECPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Dashboard metrics state
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [formData, setFormData] = useState({
    book_title: '',
    subtitle: '',
    author_name: '',
    publisher_name: '',
    genre: '',
    sub_genre: '',
    custom_sub_genre: '',
    target_audience: '',
    book_length: '',
    main_theme: '',
    tone_style: '',
    comparison_books: '',
    unique_selling_points: '',
    target_review_outlets: '',
    author_credentials: '',
    endorsement_goals: ''
  });
  
  const [file, setFile] = useState(null);
  
  const [showCustomSubGenre, setShowCustomSubGenre] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating reviews & endorsements', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  
  // TTS state
  const [audioUrl, setAudioUrl] = useState(null);
  const [currentPlayingSection, setCurrentPlayingSection] = useState(null);
  
  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch all BREC reports
        const reportsResponse = await axios.get(`${API}/api/brec/reports`, {
          withCredentials: true
        });
        setReports(reportsResponse.data || []);

        // Fetch transaction history
        const transactionsResponse = await axios.get(`${API}/api/payments/transactions`, {
          withCredentials: true
        });
        setTransactions(transactionsResponse.data || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  // Load report if report ID is in URL (from Dashboard page)
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadReport(reportId);
    }
  }, [searchParams]);

  const loadReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/api/brec/reports/${reportId}`, {
        withCredentials: true
      });
      setResult(response.data);
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Failed to load report. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'sub_genre') {
      if (value === 'Other') {
        setShowCustomSubGenre(true);
      } else {
        setShowCustomSubGenre(false);
        setFormData(prev => ({ ...prev, custom_sub_genre: '' }));
      }
    }
  };
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please upload a PDF or DOCX file');
        e.target.value = '';
        return;
      }
      
      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be under 10MB');
        e.target.value = '';
        return;
      }
      
      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.book_title.trim()) {
      setError('Please provide a book title.');
      return;
    }
    
    if (!formData.author_name.trim()) {
      setError('Please provide an author name.');
      return;
    }
    
    if (!file) {
      setError('Please upload a manuscript file (PDF or DOCX).');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const submissionData = {
      name: 'Book Review & Endorsement Creator',
      bookTitle: formData.book_title || 'BREC Generation'
    };
    
    try {
      // Check if user needs to pay BEFORE processing (only for À La Carte users)
      if (user?.role === 'a_la_carte') {
        // Show payment authorization modal BEFORE any processing or charging
        setPaymentInfo({
          amount: 7.00,  // Standard service
          currency: 'USD',
          description: 'Book Review & Endorsement Creator - AI-powered analysis',
          submission_data: submissionData,
          name: 'Book Review & Endorsement Creator (BREC)',
          serviceName: 'Book Review & Endorsement Creator (BREC)',
          bookTitle: formData.book_title || 'BREC Generation'
        });
        setShowPaymentModal(true);
        setLoading(false);
        return; // Stop here - wait for user to confirm payment
      }
      
      // Process the generation (for subscribers, admins, beta testers who don't need payment confirmation)
      await processGeneration();
      
    } catch (err) {
      console.error('BREC submission error:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while generating content.');
      setLoading(false);
    }
  };

  const processGeneration = async () => {
    try {
      // Validate required fields
      if (!formData.book_title || !formData.author_name) {
        throw new Error('Book title and author name are required');
      }
      
      if (!file) {
        throw new Error('Please upload a manuscript file (PDF or DOCX)');
      }
      
      // Step 1: Upload and analyze
      setCurrentStep('Uploading and analyzing manuscript...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: false, current: true },
        { text: 'Generating reviews & endorsements', completed: false, current: false },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      // Create FormData for multipart upload
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('title', formData.book_title);
      uploadData.append('subtitle', formData.subtitle || '');
      uploadData.append('author_name', formData.author_name);
      uploadData.append('publisher', formData.publisher_name || '');
      uploadData.append('genre', formData.genre || '');
      uploadData.append('sub_genre', formData.sub_genre || '');
      
      setProcessingProgress(25);
      
      // Step 2: Generate reviews
      setCurrentStep('Generating editorial reviews and endorsements...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating reviews & endorsements', completed: false, current: true },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/api/brec/upload-and-analyze`, uploadData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Step 3: Formatting
      setCurrentStep('Finalizing your review package...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating reviews & endorsements', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      setResult(response.data);
      
      // Complete
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating reviews & endorsements', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Show "Send Transaction Receipt" modal if transaction occurred
      if (response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
        setShowEmailReceiptModal(true);
      }
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
      toast.success('Book review and endorsement content generated successfully!');
      
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.detail || err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    setShowPaymentModal(false);
    setLoading(true);
    
    try {
      // IMPORTANT: User confirmed willingness to pay
      // BUT actual charge happens in backend AFTER successful generation
      // This ensures users are only charged for successfully completed services
      
      await processGeneration();
      
      // Note: Backend will create transaction record after successful completion
      // Transaction ID will be in the response from processGeneration
      
    } catch (error) {
      console.error('Post-payment generation error:', error);
      setError(error.response?.data?.detail || error.message || 'Generation failed. You have NOT been charged.');
      setLoading(false);
    }
  };

  const handleSendReceipt = async () => {
    try {
      await axios.post(`${API}/api/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, { withCredentials: true });
      
      toast.success('Receipt sent to your email!');
      setShowEmailReceiptModal(false);
    } catch (error) {
      console.error('Receipt email error:', error);
      toast.error('Failed to send receipt. Please try again.');
    }
  };

  const exportToPDF = () => {
    try {
      const pdf = new jsPDF('p', 'pt', 'letter');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      let yPos = 60;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Header
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('BREC Analysis Report', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 25;
      pdf.setFontSize(14);
      pdf.text(result.book_title || result.title || formData.book_title || 'Book Review & Endorsement', pageWidth / 2, yPos, { align: 'center' });
      
      if (result.author_name || result.author) {
        yPos += 20;
        pdf.setFontSize(12);
        pdf.text(`by ${result.author_name || result.author}`, pageWidth / 2, yPos, { align: 'center' });
      }

      yPos += 30;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');

      // Add each section
      const sections = [
        { title: 'Book Information Overview', content: result.book_information_overview },
        { title: 'Professional Editorial Reviews', content: result.editorial_review },
        { title: 'Reader Testimonials', content: result.reader_testimonials },
        { title: 'Strategic Endorser Recommendations', content: result.author_endorsements },
        { title: 'Professional Media Quotes', content: result.media_quotes },
        { title: 'Outreach Strategy & Implementation', content: result.final_summary }
      ];

      sections.forEach((section, index) => {
        if (section.content) {
          checkPageBreak(60);
          
          // Section title
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(section.title, margin, yPos);
          yPos += 25;

          // Section content
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(section.content, pageWidth - 2 * margin);
          
          lines.forEach((line) => {
            checkPageBreak(15);
            pdf.text(line, margin, yPos);
            yPos += 15;
          });

          yPos += 15; // Space between sections
        }
      });

      // Save PDF
      const fileName = `BREC_${result.book_title || result.title || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {!result && (
          <>
            {/* Service Title and Description */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                📚 Book Review & Endorsement Creator
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Generate professional mock reviews and strategic endorsement recommendations to build credibility. Our AI creates authentic-sounding reviews and identifies potential endorsers to strengthen your book's market position and appeal to readers.
              </p>
            </div>

            {/* Pricing Info */}
            {user && (
              <div className="mb-8 bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-lg p-6">
                <div className="text-center">
                  {user.role === 'subscriber' || user.role === 'admin' || user.role === 'beta_tester' ? (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - analyze as many books as you need!</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - $7.00</h3>
                      <p className="text-blue-700">Professional review and endorsement content charged to your payment method on file</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg mb-6 print:hidden">
            {error}
          </div>
        )}

        {!result ? (
          /* Form with Sidebar */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Book Title *</label>
                <input
                  type="text"
                  name="book_title"
                  value={formData.book_title}
                  onChange={handleInputChange}
                  placeholder="Enter your book title"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Book Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  placeholder="Enter your book subtitle (optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Author Name *</label>
                  <input
                    type="text"
                    name="author_name"
                    value={formData.author_name}
                    onChange={handleInputChange}
                    placeholder="Enter author name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Publisher Name</label>
                  <input
                    type="text"
                    name="publisher_name"
                    value={formData.publisher_name}
                    onChange={handleInputChange}
                    placeholder="Enter publisher name (optional)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Genre</label>
                  <select
                    name="genre"
                    value={formData.genre}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">Select genre...</option>
                    <option value="Fiction">Fiction</option>
                    <option value="Non-Fiction">Non-Fiction</option>
                    <option value="Business">Business</option>
                    <option value="Self-Help">Self-Help</option>
                    <option value="Biography">Biography</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Target Audience</label>
                  <input
                    type="text"
                    name="target_audience"
                    value={formData.target_audience}
                    onChange={handleInputChange}
                    placeholder="e.g., Business professionals, Young adults"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Manuscript *</label>
                <input
                  type="file"
                  accept=".pdf,.docx"
                  onChange={handleFileChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                />
                <p className="text-xs text-gray-500 mt-1">PDF or DOCX, max 10MB (Required for AI analysis)</p>
                {file && (
                  <p className="text-sm text-green-600 mt-2">✓ {file.name} selected</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating Content...' : 'Generate Review & Endorsement Content with Digital Ink AI'}
                </button>
              </div>
            </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Generation Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">📝 What You'll Get</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-cyan-500 mr-2">✓</span>
                    <span>Book information overview</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-500 mr-2">✓</span>
                    <span>5 professional editorial reviews</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-500 mr-2">✓</span>
                    <span>5 authentic reader testimonials</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-500 mr-2">✓</span>
                    <span>3 strategic endorser recommendations</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-500 mr-2">✓</span>
                    <span>5 professional media quotes</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-500 mr-2">✓</span>
                    <span>Complete outreach strategy with email templates</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Results Display - Following PDE Design Standards */
          <div className="max-w-6xl mx-auto">
            {/* Report Header - PDE Style */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 mb-6 p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">BREC Analysis Report</h1>
                  <p className="text-slate-600 text-lg">
                    Review & Endorsement Analysis for {result.book_title || result.title || formData.book_title}
                  </p>
                  {(result.author_name || result.author) && (
                    <p className="text-slate-600 mt-1">by {result.author_name || result.author}</p>
                  )}
                </div>
                <div className="flex gap-3 print:hidden">
                  <button 
                    onClick={() => window.print()} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    🖨️ Print
                  </button>
                  <button 
                    onClick={exportToPDF} 
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    📄 Export PDF
                  </button>
                  <button
                    onClick={() => setResult(null)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  >
                    ✨ New Analysis
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Book Title:</p>
                  <p className="font-bold text-slate-900">{result.book_title || result.title || formData.book_title}</p>
                </div>
                {(result.author_name || result.author) && (
                  <div>
                    <p className="text-slate-500 text-sm mb-1">Author:</p>
                    <p className="font-bold text-slate-900">{result.author_name || result.author}</p>
                  </div>
                )}
                <div>
                  <p className="text-slate-500 text-sm mb-1">Service:</p>
                  <p className="font-bold text-slate-900">Book Review & Endorsement Creator</p>
                </div>
              </div>
            </div>

            {/* Report Sections */}
            <div className="space-y-6">
              {/* Book Information Overview */}
              {result.book_information_overview && (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                  <h3 className="text-2xl font-bold text-cyan-700 mb-4 flex items-center">
                    <span className="text-3xl mr-3">📚</span>
                    Book Information Overview
                  </h3>
                  <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                    {result.book_information_overview}
                  </div>
                </div>
              )}

              {/* Editorial Reviews */}
              {result.editorial_review && (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                  <h3 className="text-2xl font-bold text-cyan-700 mb-4 flex items-center">
                    <span className="text-3xl mr-3">⭐</span>
                    Professional Editorial Reviews
                  </h3>
                  <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                    {result.editorial_review}
                  </div>
                </div>
              )}

              {/* Reader Testimonials */}
              {result.reader_testimonials && (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                  <h3 className="text-2xl font-bold text-cyan-700 mb-4 flex items-center">
                    <span className="text-3xl mr-3">💬</span>
                    Reader Testimonials
                  </h3>
                  <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                    {result.reader_testimonials}
                  </div>
                </div>
              )}

              {/* Author Endorsements */}
              {result.author_endorsements && (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                  <h3 className="text-2xl font-bold text-cyan-700 mb-4 flex items-center">
                    <span className="text-3xl mr-3">🎯</span>
                    Strategic Endorser Recommendations
                  </h3>
                  <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                    {result.author_endorsements}
                  </div>
                </div>
              )}

              {/* Media Quotes */}
              {result.media_quotes && (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                  <h3 className="text-2xl font-bold text-cyan-700 mb-4 flex items-center">
                    <span className="text-3xl mr-3">📰</span>
                    Professional Media Quotes
                  </h3>
                  <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                    {result.media_quotes}
                  </div>
                </div>
              )}

              {/* Outreach Strategy */}
              {result.final_summary && (
                <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                  <h3 className="text-2xl font-bold text-cyan-700 mb-4 flex items-center">
                    <span className="text-3xl mr-3">📧</span>
                    Outreach Strategy & Implementation
                  </h3>
                  <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                    {result.final_summary}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modals */}
        {showPaymentModal && (
          <ServiceChargeModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onConfirm={handlePaymentSuccess}
            serviceInfo={paymentInfo}
            user={user}
          />
        )}

        {loading && (
          <ProcessingModal
            isOpen={loading}
            serviceName="Book Review & Endorsement Creator (BREC)"
            bookTitle={formData.book_title}
            progress={processingProgress}
            currentStep={currentStep}
            progressSteps={progressSteps}
          />
        )}

        {showEmailReceiptModal && lastTransactionId && (
          <EmailReceiptModal
            isOpen={showEmailReceiptModal}
            onClose={() => setShowEmailReceiptModal(false)}
            onSendReceipt={handleSendReceipt}
            serviceName="Book Review & Endorsement Creator (BREC)"
            amount={result?.charge_amount || 7.00}
            bookTitle={result?.book_title || result?.title || formData.book_title}
          />
        )}
      </main>
    </div>
  );
}

export default BRECPage;